import { useState, useMemo, useEffect } from "react";
import Select, { SingleValue } from "react-select";
import styles from "./userDebtModal.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { IPopupWithoutProps } from "../../../types/types";
import Loader from "../../loader/loader";
import ExportTableButtons from "../../exportTableButtons/ExportTableButtons";
import {
  useLazyGetTreatmentsWithoutPaymentQuery,
  useChargeTokenMutation,
  useGetPaymentTypesQuery,
} from "../../../services/api/utils.api";
import {
  useGetActiveQuestionnaireScoreStatusesQuery,
  useUpdateQuestionnaireScoreTotalAmountMutation,
  useCheckUserPaymentTokenPaymentsApiQuery,
} from "../../../services/api/payment.api";
import { ITreatmentDebtItem } from "../../../services/types/utils.types";
import { ICreatePaymentRequest } from "../../../services/types/utils.types";
import { TUpdateQuestionnaireScoreTotalAmountRequest } from "../../../services/types/payment.types";
import {
  distributePaymentAcrossTreatments,
  validatePaymentItems,
  calculateTotalDebt,
} from "../../../utils/paymentDistribution";
import { roundPrice } from "../../../utils/constants";
import { getPrintDate } from "../../../utils/utils";

interface UserDebtModalProps extends IPopupWithoutProps {
  userGuid: string;
  userName: string;
  userId: number;
  onPaymentSuccess?: () => void;
  onViewPaymentHistory?: () => void;
}

const EXCLUDED_STATUS_IDS = new Set([2, 4, 6, 8]);

function UserDebtModal({
  onClose,
  userGuid,
  userName,
  userId,
  onPaymentSuccess,
  onViewPaymentHistory,
}: UserDebtModalProps) {
  const [fetchTreatments, { data: treatments, isLoading, error }] =
    useLazyGetTreatmentsWithoutPaymentQuery();
  const [chargeToken] = useChargeTokenMutation();
  const { data: paymentTypes, isLoading: isLoadingPaymentTypes } = useGetPaymentTypesQuery();
  const { data: tokenStatus } = useCheckUserPaymentTokenPaymentsApiQuery(userGuid);
  const { data: statuses, isLoading: isLoadingStatuses } = useGetActiveQuestionnaireScoreStatusesQuery(undefined);
  const [updateQuestionnaireScoreTotalAmount, { isLoading: isUpdating }] =
    useUpdateQuestionnaireScoreTotalAmountMutation();

  const [editedDebts, setEditedDebts] = useState<Record<number, number>>({});
  const [amountDrafts, setAmountDrafts] = useState<Record<number, string>>({});
  const [statusDrafts, setStatusDrafts] = useState<Record<number, number>>({});
  const [selectedQcsuIds, setSelectedQcsuIds] = useState<Set<number>>(new Set());
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [selectedPaymentTypeId, setSelectedPaymentTypeId] = useState<number | null>(null);
  const [chargeAmount, setChargeAmount] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [sendInvoice, setSendInvoice] = useState<boolean>(true);

  // Check if user has a credit card token
  const userHasCreditCard = tokenStatus?.hasToken ?? false;

  // Status options and mapping
  type TStatusOption = { value: number; label: string };
  const statusOptions: TStatusOption[] = useMemo(
    () => (statuses ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statuses]
  );

  const statusNameById = useMemo(() => {
    if (!statuses) return {} as Record<number, string>;
    return statuses.reduce((acc, s) => {
      acc[s.id] = s.name;
      return acc;
    }, {} as Record<number, string>);
  }, [statuses]);

  const statusIdByName = useMemo(() => {
    if (!statuses) return {} as Record<string, number>;
    return statuses.reduce((acc, s) => {
      if (s.name) acc[s.name] = s.id;
      return acc;
    }, {} as Record<string, number>);
  }, [statuses]);

    // Filter out treatments whose persisted status ID is in the excluded set (2, 4, 6, 8).
    // Use only the original status (not the draft) so a row with a pending status change
    // stays visible and the save button remains accessible.
    const filteredTreatments = useMemo(() => {
      if (!treatments) return undefined;
      return treatments.filter((item) => {
        const statusId = item.statusName
          ? (statusIdByName[item.statusName] ?? null)
          : null;
        if (statusId === null) return true;
        return !EXCLUDED_STATUS_IDS.has(statusId);
      });
    }, [treatments, statusIdByName]);
   

  // Select styles matching the modal's design
  const filterSelectStyles = {
    menu: (base: object) => ({ ...base, fontSize: "13px", zIndex: 10001, borderRadius: "6px", border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", marginTop: 2, minWidth: "140px" }),
    menuPortal: (base: object) => ({ ...base, zIndex: 10001 }),
    menuList: (base: object) => ({ ...base, padding: "4px" }),
    option: (base: object, state: any) => ({
      ...base,
      fontSize: "12px",
      padding: "6px 10px",
      borderRadius: "4px",
      cursor: "pointer",
      backgroundColor: state.isSelected ? "#f3f4f6" : state.isFocused ? "#f9fafb" : "transparent",
      color: "#374151",
      fontWeight: state.isSelected ? 500 : 400,
    }),
    singleValue: (base: object) => ({ ...base, fontSize: "13px", color: "#374151" }),
    control: (base: object, state: any) => ({
      ...base,
      minHeight: "30px",
      fontSize: "13px",
      borderRadius: "6px",
      border: "1px solid #e5e7eb",
      boxShadow: "none",
      backgroundColor: "#f9fafb",
      cursor: "pointer",
      "&:hover": { backgroundColor: "#f3f4f6" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base: object) => ({
      ...base,
      padding: "0 4px",
      color: "#9ca3af",
      svg: { width: 14, height: 14 },
    }),
    placeholder: (base: object) => ({ ...base, fontSize: "13px", color: "#9ca3af" }),
    valueContainer: (base: object) => ({ ...base, padding: "0 8px" }),
  };

  // Set default payment type once we know if user has credit card
  useEffect(() => {
    if (selectedPaymentTypeId === null && paymentTypes && paymentTypes.length > 0) {
      // If user has credit card, default to credit card (1), otherwise default to Cash (3)
      if (userHasCreditCard) {
        setSelectedPaymentTypeId(1); // Credit Card
      } else {
        // Default to Cash (3), fallback to Bank Transfer (2), then first available
        const cashOption = paymentTypes.find(pt => pt.id === 3);
        const bankTransferOption = paymentTypes.find(pt => pt.id === 2);
        setSelectedPaymentTypeId(cashOption?.id ?? bankTransferOption?.id ?? paymentTypes[0].id);
      }
    }
  }, [paymentTypes, userHasCreditCard, selectedPaymentTypeId]);

  // Fetch treatments on mount
  useEffect(() => {
    fetchTreatments({ userGuid });
  }, [fetchTreatments, userGuid]);

  // Calculate total debt (using edited values if available)
  const totalDebt = useMemo(() => {
    if (!filteredTreatments) return 0;
    return filteredTreatments.reduce((sum, item) => {
      const editedDebt = editedDebts[item.qcsu.id];
      return sum + (editedDebt !== undefined ? editedDebt : item.debt);
    }, 0);
  }, [filteredTreatments, editedDebts]);

  const allQcsuIds = useMemo(() => (filteredTreatments  ?? []).map((t) => t.qcsu.id), [filteredTreatments ]);
  const allSelected = allQcsuIds.length > 0 && allQcsuIds.every((id) => selectedQcsuIds.has(id));
  const someSelected = allQcsuIds.some((id) => selectedQcsuIds.has(id));

  // Debt sum for only the checked rows
  const selectedDebt = useMemo(() => {
    if (!treatments) return 0;
    return treatments
      .filter((item) => selectedQcsuIds.has(item.qcsu.id))
      .reduce((sum, item) => {
        const editedDebt = editedDebts[item.qcsu.id];
        return sum + (editedDebt !== undefined ? editedDebt : item.debt);
      }, 0);
  }, [treatments, editedDebts, selectedQcsuIds]);

  // Sync charge amount: use selected-rows debt when any are checked, otherwise full debt
  useEffect(() => {
    setChargeAmount(someSelected ? selectedDebt : totalDebt);
  }, [selectedDebt, totalDebt, someSelected]);

  const handleDebtChange = (qcsuId: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    setEditedDebts((prev) => ({
      ...prev,
      [qcsuId]: numValue,
    }));
  };

  const toggleQcsuId = (id: number) => {
    setSelectedQcsuIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedQcsuIds(allSelected ? new Set() : new Set(allQcsuIds));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getTherapistName = (item: ITreatmentDebtItem) => {
    return item.therapistName || "-";
  };

  const getStatusId = (item: ITreatmentDebtItem): number | null => {
    // Check if we have a draft status for this item
    if (statusDrafts[item.qcsu.id] !== undefined) {
      return statusDrafts[item.qcsu.id];
    }
    // Try to get status ID from status name
    if (item.statusName) {
      return statusIdByName[item.statusName] ?? null;
    }
    return null;
  };

  const getOriginalStatusId = (item: ITreatmentDebtItem): number | null =>
    item.statusName ? statusIdByName[item.statusName] ?? null : null;

  const isRowDebtEditDirty = (item: ITreatmentDebtItem): boolean => {
    const id = item.qcsu.id;
    if (!(id in amountDrafts)) return false;
    const raw = amountDrafts[id];
    const num = raw === "" ? null : roundPrice(Number(raw));
    const currentAmount = item.qcsu.totalAmount ?? null;
    return num !== currentAmount;
  };

  const isRowStatusEditDirty = (item: ITreatmentDebtItem): boolean => {
    const draft = statusDrafts[item.qcsu.id];
    if (draft === undefined) return false;
    return draft !== getOriginalStatusId(item);
  };

  const isRowSavePending = (item: ITreatmentDebtItem): boolean =>
    isRowDebtEditDirty(item) || isRowStatusEditDirty(item);

  const commitRowEdits = async (item: ITreatmentDebtItem) => {
    const id = item.qcsu.id;
    const originalStatusId = getOriginalStatusId(item);
    const draftStatus = statusDrafts[id];
    const statusChanged =
      draftStatus !== undefined && draftStatus !== originalStatusId;

    let amountPatch: number | undefined;
    if (id in amountDrafts) {
      const raw = amountDrafts[id];
      const num = raw === "" ? null : roundPrice(Number(raw));
      const currentAmount = item.qcsu.totalAmount ?? null;
      if (num !== currentAmount) {
        amountPatch = num ?? 0;
      }
    }

    if (!statusChanged && amountPatch === undefined) return;

    await handleUpdateTreatment(item, {
      questionnaireCompaniesScoreUsersStatusesId: statusChanged ? draftStatus : undefined,
      totalAmount: amountPatch,
    });
  };

  const buildUpdateBody = (
    item: ITreatmentDebtItem,
    patch: {
      questionnaireCompaniesScoreUsersStatusesId?: number;
      totalAmount?: number;
    }
  ): TUpdateQuestionnaireScoreTotalAmountRequest => ({
    totalAmount: patch.totalAmount ?? item.qcsu.totalAmount ?? 0,
    companyServiceId: item.qcsu.companyServiceId ?? undefined,
    questionnaireCompaniesScoreUsersStatusesId:
      patch.questionnaireCompaniesScoreUsersStatusesId ?? getStatusId(item) ?? undefined,
  });

  const handleUpdateTreatment = async (
    item: ITreatmentDebtItem,
    patch: {
      questionnaireCompaniesScoreUsersStatusesId?: number;
      totalAmount?: number;
    }
  ) => {
    try {
      const body = buildUpdateBody(item, patch);
      await updateQuestionnaireScoreTotalAmount({
        id: item.qcsu.id,
        body,
      }).unwrap();
      // Refetch treatments to get updated data
      await fetchTreatments({ userGuid });
      const qcsuId = item.qcsu.id;
      setStatusDrafts((prev) => {
        if (!(qcsuId in prev)) return prev;
        const next = { ...prev };
        delete next[qcsuId];
        return next;
      });
      setAmountDrafts((prev) => {
        if (!(qcsuId in prev)) return prev;
        const next = { ...prev };
        delete next[qcsuId];
        return next;
      });
    } catch (err) {
      console.error("Failed to update treatment:", err);
    }
  };

  const handlePay = async () => {
    if (chargeAmount <= 0) return;

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Build payment items using distribution logic
      // This handles 3 cases: amount < debt, amount = debt, amount > debt (overpayment)
      let items;
      if (filteredTreatments  && filteredTreatments .length > 0) {
        // Only distribute across selected treatments when user has checked specific rows
        const treatmentsToCharge = someSelected
          ? filteredTreatments.filter((t) => selectedQcsuIds.has(t.qcsu.id))
          : filteredTreatments;

        // Apply edited debts to create modified treatments for distribution
        const modifiedTreatments = treatmentsToCharge.map((t) => ({
          ...t,
          debt: editedDebts[t.qcsu.id] !== undefined ? editedDebts[t.qcsu.id] : t.debt,
        }));

        items = distributePaymentAcrossTreatments(modifiedTreatments, chargeAmount);
      } else {
        // No treatments - use single item matching charge amount (overpayment/credit)
        items = [{
          CompanyServicesId: null,
          ProductDesc: "יתרת זכות",
          Date: null,
          TherapistName: null,
          QuestionnaireCompaniesScoreUsersId: null,
          UnitCost: chargeAmount
        }];
      }

      // Validate that items sum equals payment amount
      const validation = validatePaymentItems(items, chargeAmount);
      if (!validation.isValid) {
        setPaymentError(t("PAYMENT_VALIDATION_ERROR"));
        setIsProcessingPayment(false);
        return;
      }

      const request: ICreatePaymentRequest = {
        UsersGuid: userGuid,
        SlikaType: 2, // Token - for ChargeToken endpoint
        Operation: "ChargeOnly",
        Amount: chargeAmount,
        DocumentType: 1, // 1 = TaxInvoiceAndReceipt
        PaymentsTypesId: selectedPaymentTypeId ?? undefined,
        Items: items,
        IsGeneralPay: true, // Updates user balance and allocates to oldest debts
        Comments: comment || undefined,
        QcsuIds: selectedQcsuIds.size > 0 ? Array.from(selectedQcsuIds) : undefined,
        SendInvoice: sendInvoice, // When false, the receipt/invoice is created but not emailed/SMS'd to the patient
      };

      const result = await chargeToken(request).unwrap();

      // Handle both PascalCase and camelCase response from backend
      const responseCode = result.ResponseCode ?? result.responseCode;
      const description = result.Description ?? result.description;

      if (responseCode === 0) {
        // ChargeToken doesn't return a URL for credit card payments (charges directly)
        // For non-credit card payments (cash, check, etc.) it just creates a document
        onPaymentSuccess?.();
        onClose();
      } else {
        setPaymentError(description || t("PAYMENT_OPERATION_FAILED"));
      }
    } catch (err: any) {
      setPaymentError(
        err?.data?.message || err?.message || t("PAYMENT_OPERATION_FAILED")
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <article className={styles.container}>
      <h2 className={styles.title}>
        {t("USER_DEBT_DETAILS")} - {userName}
      </h2>

      {isLoading && <p className={styles.loading}>{t("LOADING")}...</p>}

      {error && (
        <p className={styles.error}>
          {t("ERROR_LOADING_DATA")}
        </p>
      )}

      {/* Debt table - only shown if there are treatments with debt */}
      {!isLoading && !error && filteredTreatments  && filteredTreatments .length > 0 && (
        <>
          <div className={styles.exportRow}>
            <ExportTableButtons
              entity="UserDebtDetails"
              title={`${t("USER_DEBT_DETAILS")} - ${userName}`}
              dataRows={[
                ...filteredTreatments.map((item) => ({
                  TreatmentType: item.companyServiceName || "-",
                  Therapist: getTherapistName(item),
                  SessionDate: formatDate(item.qcsu.timestamp),
                  AppointmentDate: item.appointmentDate ? formatDate(item.appointmentDate) : "",
                  Status: statusNameById[getStatusId(item) ?? 0] || item.statusName || "-",
                  TotalCost: item.qcsu.totalAmount ?? 0,
                  Paid: item.qcsu.totalAmountPay ?? 0,
                  RemainingDebt: editedDebts[item.qcsu.id] !== undefined ? editedDebts[item.qcsu.id] : item.debt,
                })),
                {
                  TreatmentType: t("TOTAL_DEBT"),
                  Therapist: "",
                  SessionDate: "",
                  AppointmentDate: "",
                  Status: "",
                  TotalCost: "",
                  Paid: "",
                  RemainingDebt: totalDebt.toFixed(2),
                },
              ]}
            />
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                      onChange={toggleSelectAll}
                      aria-label={t("SELECT_ALL")}
                    />
                  </th>
                  <th>{t("TREATMENT_TYPE")}</th>
                  <th>{t("THERAPIST")}</th>
                  <th>{t("REPORT_SESSION_DATE")}</th>
                  <th>{t("REPORT_APPOINTMENT_DATE")}</th>
                  <th>{t("STATUS")}</th>
                  <th>{t("TOTAL_COST")}</th>
                  <th>{t("PAID")}</th>
                  <th>{t("REMAINING_DEBT")}</th>
                  <th className={styles.rowActionsHead} aria-hidden />
                </tr>
              </thead>
              <tbody>
                {filteredTreatments.map((item) => (
                  <tr key={item.qcsu.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedQcsuIds.has(item.qcsu.id)}
                        onChange={() => toggleQcsuId(item.qcsu.id)}
                        aria-label={`${t("SELECT")} ${item.companyServiceName || item.qcsu.id}`}
                      />
                    </td>
                    <td>{item.companyServiceName || "-"}</td>
                    <td>{getTherapistName(item)}</td>
                    <td>{getPrintDate(new Date(item.qcsu.timestamp))}</td>
                    <td>{item.appointmentDate ? getPrintDate(new Date(item?.appointmentDate)) : ""}</td>
                    <td>
                      {isLoadingStatuses ? (
                        <span>{t("LOADING")}...</span>
                      ) : (
                        <span
                          className={styles.statusSelectWrapper}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Select<TStatusOption>
                            classNamePrefix="debt-status"
                            options={statusOptions}
                            value={
                              statusOptions.find((o) => o.value === getStatusId(item)) ?? null
                            }
                            onChange={(selected: SingleValue<TStatusOption>) => {
                              if (!selected) return;
                              const currentId = getStatusId(item);
                              if (selected.value !== currentId) {
                                setStatusDrafts((prev) => ({
                                  ...prev,
                                  [item.qcsu.id]: selected.value,
                                }));
                              }
                            }}
                            isDisabled={isUpdating}
                            isSearchable={false}
                            isClearable={false}
                            placeholder={item.statusName || t("STATUS")}
                            aria-label={t("STATUS")}
                            menuPlacement="auto"
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={filterSelectStyles}
                          />
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={styles.amountInputWrap}>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={styles.inlineAmountInput}
                          value={
                            item.qcsu.id in amountDrafts
                              ? amountDrafts[item.qcsu.id]
                              : item.qcsu.totalAmount !== null && item.qcsu.totalAmount !== undefined
                                ? item.qcsu.totalAmount
                                : ""
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            setAmountDrafts((prev) => ({
                              ...prev,
                              [item.qcsu.id]: e.target.value,
                            }));
                          }}
                          onBlur={(e) => {
                            e.stopPropagation();
                            void commitRowEdits(item);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isUpdating}
                          aria-label={t("TOTAL_COST")}
                        />
                      ₪

                      </span>
                    </td>
                    <td>{item.qcsu.totalAmountPay ?? 0}</td>
                    <td>{item.debt}</td>
                    <td className={styles.rowActionsCell}>
                      {isRowSavePending(item) && (
                        <button
                          type="button"
                          className={styles.rowSubmitBtn}
                          disabled={isUpdating}
                          onClick={(e) => {
                            e.stopPropagation();
                            void commitRowEdits(item);
                          }}
                          aria-label={t("DEBT_ROW_SUBMIT")}
                        >
                          {t("DEBT_ROW_SUBMIT")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.summary}>
            <span className={styles.totalLabel}>{t("TOTAL_DEBT")}:</span>
            <span className={styles.totalValue}>
              {totalDebt.toFixed(2)}
            </span>
          </div>
        </>
      )}

      {!isLoading && !error && treatments && treatments.length === 0 && (
        <p className={styles.noData}>{t("NO_OUTSTANDING_DEBT")}</p>
      )}

      {/* Payment section - always visible */}
      {!isLoading && !error && (
        <>
          <div className={styles.chargeAmountSection}>
            <div className={styles.commentWrapper}>
              <label className={styles.commentLabel}>{t("COMMENT")}:</label>
              <textarea
                className={styles.commentInput}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("OPTIONAL_COMMENT")}
                rows={1}
              />
            </div>
            <div className={styles.amountWrapper}>
              <label className={styles.chargeAmountLabel}>{t("AMOUNT_TO_CHARGE")}:</label>
              <input
                type="number"
                className={styles.chargeAmountInput}
                value={chargeAmount || ''}
                onChange={(e) => setChargeAmount(parseFloat(e.target.value) || 0)}
                min={0}
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className={styles.paymentTypeSection}>
            <span className={styles.paymentTypeLabel}>{t("PAYMENT_TYPE")}:</span>
            {isLoadingPaymentTypes ? (
              <span className={styles.loading}>{t("LOADING")}...</span>
            ) : (
              <div className={styles.paymentTypeOptions}>
                {/* Sort payment types: Credit Card (1), Cash (3), Bank Transfer (2), then others */}
                {[...(paymentTypes || [])].sort((a, b) => {
                  const order: Record<number, number> = { 1: 0, 3: 1, 2: 2 }; // Credit Card, Cash, Bank Transfer
                  return (order[a.id] ?? 99) - (order[b.id] ?? 99);
                }).map((paymentType) => {
                  const isCreditCard = paymentType.id === 1;
                  const isTransfer = paymentType.id == 2;
                  const isDisabled = (isCreditCard && !userHasCreditCard);
                  return (
                    <label
                      key={paymentType.id}
                      className={`${styles.radioLabel} ${isDisabled ? styles.radioLabelDisabled : ''}`}
                      title={(isDisabled) ? t("NO_CREDIT_CARD") : undefined}
                    >
                      <input
                        type="radio"
                        name="paymentType"
                        value={paymentType.id}
                        checked={selectedPaymentTypeId === paymentType.id}
                        onChange={() => setSelectedPaymentTypeId(paymentType.id)}
                        className={styles.radioInput}
                        disabled={isDisabled}
                      />
                      <span className={styles.radioText}>{paymentType.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.sendInvoiceSection}>
            <label className={styles.sendInvoiceLabel}>
              <input
                type="checkbox"
                className={styles.sendInvoiceCheckbox}
                checked={sendInvoice}
                onChange={(e) => setSendInvoice(e.target.checked)}
              />
              <span>{t("SEND_INVOICE_TO_PATIENT")}</span>
            </label>
          </div>

          {paymentError && (
            <p className={styles.paymentError}>{paymentError}</p>
          )}

          <div className={styles.actions}>
            {onViewPaymentHistory && (
              <button
                type="button"
                className={styles.historyBtn}
                onClick={onViewPaymentHistory}
                disabled={isProcessingPayment}
              >
                {t("VIEW_PAYMENT_HISTORY")}
              </button>
            )}
            <div className={styles.actionsRight}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
                disabled={isProcessingPayment}
              >
                {t("CANCEL")}
              </button>
              <button
                type="button"
                className={styles.payBtn}
                onClick={handlePay}
                disabled={isProcessingPayment || chargeAmount <= 0}
                title={undefined}
              >
                {isProcessingPayment ? t("PROCESSING") : t("PAY")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Loading overlay when updating */}
      {isUpdating && (
        <div className={styles.loadingOverlay}>
          <Loader />
        </div>
      )}
    </article>
  );
}

export default UserDebtModal;
